import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, MoreHorizontal, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { ACTION_LABELS, BLOCKED_MESSAGES, HISTORY_LABELS, formatDay, governanceError } from "../lib/governance";
import { formatDateTime } from "../lib/tickets";
import { ActionDialog } from "../components/governance/DecisionDialogs";
import { DecisionState, Field, LinkList } from "../components/governance/GovernanceBits";
import LinkedActions from "../components/governance/LinkedActions";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

function Block({ title, children, action, testid }) {
  return (
    <Card className="p-4 sm:p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2"><h2 className="text-base font-semibold text-slate-900">{title}</h2>{action}</div>
      {children}
    </Card>
  );
}

export default function GovernanceDecision() {
  const { decisionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [decision, setDecision] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/governance/decisions/${decisionId}`);
      setDecision(data);
      setPhase("ready");
    } catch (e) {
      setPhase(e?.response?.status === 404 ? "missing" : "error");
    }
  }, [decisionId]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-40 w-full" /></div>;
  if (phase === "missing") {
    return (
      <Card className="p-8 text-center space-y-2" data-testid="decision-missing">
        <h2 className="font-medium text-slate-900">Décision introuvable</h2>
        <p className="text-sm text-slate-500">Elle n'existe pas, ou vous n'y avez pas accès.</p>
        <Button asChild variant="outline"><Link to="/gouvernance/decisions">Retour aux décisions</Link></Button>
      </Card>
    );
  }
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="decision-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">Impossible de charger cette décision.</p>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }

  const cap = decision.capabilities;
  const has = (a) => cap.actions.includes(a);
  const canDelete = decision.status === "proposed" && hasPermission(user, "governance:delete");
  const primary = has("adopt") ? "adopt" : has("repropose") ? "repropose" : null;
  const secondary = ["defer", "cancel", "repropose"].filter((a) => has(a) && a !== primary);
  const blockedNote = cap.blocked.adopt || cap.blocked.cancel;
  const done = (data) => { setDialog(null); setDecision(data); };

  const patchLinks = async (links) => {
    try {
      const { data } = await api.patch(`/governance/decisions/${decision.id}`, { expected_version: decision.version, links: links.map((l) => ({ kind: l.kind, ref_id: l.ref_id })) });
      setDecision(data);
    } catch (e) {
      toast.error(governanceError(e, "Impossible de retirer ce lien."));
    }
  };

  return (
    <div className="space-y-4" data-testid="decision-page">
      <div>
        <Link to="/gouvernance/decisions" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Décisions</Link>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 tabular-nums" data-testid="decision-number">{decision.number}</p>
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="decision-title">{decision.title}</h1>
            <div className="mt-1.5"><DecisionState decision={decision} /></div>
          </div>
          <div className="flex items-center gap-2">
            {primary && <Button onClick={() => setDialog(primary)} data-testid={`decision-${primary}`}><Check size={15} className="mr-1.5" />{ACTION_LABELS[primary]}</Button>}
            {cap.edit_people && (
              <Button asChild variant="outline" data-testid="decision-edit"><Link to={`/gouvernance/decisions/${decision.id}/modifier`}><Pencil size={14} className="mr-1.5" />Modifier</Link></Button>
            )}
            {(secondary.length > 0 || cap.replace || canDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="icon" aria-label="Autres actions" data-testid="decision-more"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {secondary.map((a) => <DropdownMenuItem key={a} onClick={() => setDialog(a)} data-testid={`decision-${a}`} className={a === "cancel" ? "text-red-600" : undefined}>{ACTION_LABELS[a]}</DropdownMenuItem>)}
                  {cap.replace && <DropdownMenuItem onClick={() => navigate(`/gouvernance/decisions/nouvelle?remplace=${decision.id}`)} data-testid="decision-replace">Proposer un remplacement</DropdownMenuItem>}
                  {canDelete && <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="decision-delete">Supprimer la décision…</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {blockedNote && <p className="mt-2 text-sm text-slate-500" data-testid="decision-blocked">{BLOCKED_MESSAGES[blockedNote]}</p>}
        {decision.status === "deferred" && <p className="mt-2 text-sm text-amber-800" data-testid="decision-deferred-note">Cette décision n'est pas tranchée. Elle ne déclenche rien et ne modifie aucun montant ailleurs.</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <div className="space-y-4 min-w-0">
          {decision.context && <Block title="Contexte"><p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="decision-context">{decision.context}</p></Block>}
          {decision.options.length > 0 && (
            <Block title="Options examinées" testid="decision-options">
              <div className="grid gap-3 md:grid-cols-2">
                {decision.options.map((o) => (
                  <div key={o.id} className={`rounded-md border p-3 space-y-1.5 ${decision.chosen_option_id === o.id ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`option-${o.id}`}>
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">{o.label}{decision.chosen_option_id === o.id && <span className="text-xs font-medium text-emerald-700">Retenue</span>}</div>
                    {o.pros && <p className="text-sm text-slate-700"><span className="text-slate-500">Pour : </span>{o.pros}</p>}
                    {o.cons && <p className="text-sm text-slate-700"><span className="text-slate-500">Contre : </span>{o.cons}</p>}
                  </div>
                ))}
              </div>
            </Block>
          )}
          {decision.decision && (
            <Block title="Décision" testid="decision-outcome">
              <p className="text-sm text-slate-900 whitespace-pre-wrap" data-testid="decision-text">{decision.decision}</p>
              {decision.justification && <div><h3 className="text-xs uppercase tracking-wide text-slate-500 mb-1">Justification</h3><p className="text-sm text-slate-800 whitespace-pre-wrap">{decision.justification}</p></div>}
            </Block>
          )}
          <LinkedActions base={`/governance/decisions/${decision.id}`} tasks={decision.tasks} canLink={cap.link_tasks} reference={decision.number} onData={setDecision} />
          {decision.links.length > 0 && (
            <Block title="Liens" testid="decision-links">
              <LinkList links={decision.links} onRemove={cap.edit_people ? (l) => patchLinks(decision.links.filter((x) => x !== l)) : undefined} />
              <p className="text-xs text-slate-500">Un lien ne modifie rien dans l'autre module : ni montant, ni statut, ni paiement.</p>
            </Block>
          )}
          <Block title="Historique" testid="decision-history">
            <ol className="space-y-2">
              {decision.history.map((h) => (
                <li key={h.id} className="border-l-2 border-slate-200 pl-3">
                  <div className="text-sm text-slate-900">{HISTORY_LABELS[h.kind] || h.kind}</div>
                  <div className="text-xs text-slate-500">{formatDateTime(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}</div>
                  {h.reason && <div className="text-xs text-slate-600">Motif : {h.reason}</div>}
                </li>
              ))}
            </ol>
          </Block>
        </div>

        <Card className="p-4 lg:sticky lg:top-4" data-testid="decision-side">
          <h2 className="text-base font-semibold text-slate-900 mb-1">Fiche</h2>
          <dl className="divide-y divide-slate-100">
            <Field label="Proposée par">{decision.author.name || "—"}</Field>
            <Field label="Le">{formatDay(decision.created_at)}</Field>
            {decision.meeting && <Field label="Réunion"><Link className="text-jamora hover:underline" to={`/gouvernance/reunions/${decision.meeting.id}`} data-testid="decision-meeting">{decision.meeting.number}</Link></Field>}
            <Field label="Décideurs">{decision.deciders.length ? decision.deciders.map((d) => d.name).join(", ") : "Non précisés"}</Field>
            {decision.interested.length > 0 && <Field label="Concernés">{decision.interested.map((p) => p.name).join(", ")}</Field>}
            {decision.decided_on && <Field label="Décidée le">{formatDay(decision.decided_on)}</Field>}
            {decision.review_on && decision.status === "deferred" && <Field label="Revue le">{formatDay(decision.review_on)}</Field>}
            {decision.supersedes && <Field label="Remplace"><Link className="text-jamora hover:underline" to={`/gouvernance/decisions/${decision.supersedes.id}`} data-testid="decision-supersedes">{decision.supersedes.number}</Link></Field>}
            {decision.replaced_by && <Field label="Remplacée par"><Link className="text-jamora hover:underline" to={`/gouvernance/decisions/${decision.replaced_by.id}`} data-testid="decision-replaced-by">{decision.replaced_by.number}</Link></Field>}
          </dl>
        </Card>
      </div>

      {dialog && dialog !== "delete" && <ActionDialog action={dialog} decision={decision} onClose={() => setDialog(null)} onDone={done} />}
      <DeleteWithReasonDialog
        target={dialog === "delete" ? decision : null} testId="delete-governance" onClose={() => setDialog(null)}
        title={`Supprimer la décision ${decision.number} ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Une décision adoptée, ou qui a des actions liées, se remplace ou s'annule à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/governance/decisions/${decision.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            navigate("/gouvernance/decisions");
          } catch (e) {
            toast.error(governanceError(e, "Impossible de supprimer cet élément."));
            setDialog(null);
          }
        }}
      />
    </div>
  );
}
